<div class="setnew-root">
  <div class="setnew-columns">
    <div class="setnew-image-col" aria-hidden="true">
      <app-auth-hero-panel [hero]="hero" />
    </div>
    <div class="setnew-form-col">
      <div class="setnew-form-wrapper">
        <div class="logo-container">
          <img src="assets/icons/aio-logo-black.svg" alt="Logo" class="setnew-logo" />
        </div>

        @if (loading() && !tokenValid() && !tokenError()) {
          <div class="loading-message">
            <span class="button-spinner dark"></span>
            Validating reset link…
          </div>
        }

        @if (tokenError()) {
          <div class="token-error-state">
            <div class="error-icon-wrap">
              <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="#f87171" stroke-width="1.5"/>
                <path d="M15 9L9 15M9 9L15 15" stroke="#f87171" stroke-width="1.5" stroke-linecap="round"/>
              </svg>
            </div>
            <h2 class="setnew-state-title">Invalid reset link</h2>
            <p class="setnew-state-text">{{ tokenError() }}</p>
            <button type="button" class="setnew-button" (click)="requestNewLink()">
              Request New Link
            </button>
          </div>
        }

        @if (submitError()) {
          <div class="submit-error" role="alert">
            {{ submitError() }}
          </div>
        }

        @if (tokenValid()) {
          <div class="setnew-header">
            <h2 class="setnew-title">Set New Password</h2>
            <p class="setnew-subtext">Choose a strong password of at least 8 characters with one special character and uppercase letter.</p>
          </div>

          <form [formGroup]="passwordForm" class="setnew-form" (ngSubmit)="onSubmit()">
            <div class="form-group">
              <label for="password" class="form-label">New Password</label>
              <div class="input-icon-group">
                <input
                  id="password"
                  type="{{ passwordVisible() ? 'text' : 'password' }}"
                  class="form-input"
                  [class.input-error]="isPasswordError()"
                  placeholder="Min. 8 characters"
                  formControlName="password"
                  (input)="onPasswordChange()"
                  (blur)="onPasswordBlur()"
                  autocomplete="new-password"
                />
                <span
                  class="input-icon eye-icon"
                  [class.eye-open]="passwordVisible()"
                  (click)="passwordVisible.set(!passwordVisible())"
                  tabindex="0"
                  role="button"
                  aria-label="Show/Hide password"
                ></span>
              </div>
              <ul class="password-requirements" aria-live="polite">
                <li class="password-requirement">
                  <span class="requirement-dot" [class.met]="hasMinLength()" aria-hidden="true">
                    @if (hasMinLength()) {
                      <svg viewBox="0 0 10 10" class="requirement-check" aria-hidden="true">
                        <path d="M2 5l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                  </span>
                  <span class="requirement-label">At least 8 characters</span>
                </li>
                <li class="password-requirement">
                  <span class="requirement-dot" [class.met]="hasMixedCase()" aria-hidden="true">
                    @if (hasMixedCase()) {
                      <svg viewBox="0 0 10 10" class="requirement-check" aria-hidden="true">
                        <path d="M2 5l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                  </span>
                  <span class="requirement-label">Upper and lowercase</span>
                </li>
                <li class="password-requirement">
                  <span class="requirement-dot" [class.met]="hasSpecialCharacter()" aria-hidden="true">
                    @if (hasSpecialCharacter()) {
                      <svg viewBox="0 0 10 10" class="requirement-check" aria-hidden="true">
                        <path d="M2 5l2 2 4-4" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    }
                  </span>
                  <span class="requirement-label">One special character</span>
                </li>
              </ul>
            </div>

            <div class="form-group">
              <label for="confirmPassword" class="form-label">Confirm Password</label>
              <div class="input-icon-group">
                <input
                  id="confirmPassword"
                  type="{{ confirmPasswordVisible() ? 'text' : 'password' }}"
                  class="form-input"
                  [class.input-error]="isConfirmPasswordError()"
                  placeholder="Re-enter password"
                  formControlName="confirmPassword"
                  autocomplete="new-password"
                />
                <span
                  class="input-icon eye-icon"
                  [class.eye-open]="confirmPasswordVisible()"
                  (click)="confirmPasswordVisible.set(!confirmPasswordVisible())"
                  tabindex="0"
                  role="button"
                  aria-label="Show/Hide password"
                ></span>
              </div>
              @if (showSpecialCharError() && (!hasSpecialCharacter() || !hasUppercaseLetter())) {
                <div class="field-error">
                  Allowed special characters: !&#64;#$%^&
                </div>
              }
              @if (confirmPassword() && password() !== confirmPassword()) {
                <div class="field-error">
                  Passwords do not match
                </div>
              }
              @if (showPasswordRepeatError()) {
                <div class="field-error">
                  You cannot repeat a password you've used previously
                </div>
              }
            </div>

            <aio-button [block]="true" appearance="brand" size="lg" type="submit" [disabled]="!canSubmit() || loading()" class="setnew-button">
              @if (loading()) {
                <span class="button-spinner"></span>
                Updating…
              } @else {
                Reset Password
              }
            </aio-button>

            <aio-button [block]="true" appearance="neutral-stroke" size="lg" type="button" class="back-to-login-button" (pressed)="goBack()">
              <svg class="back-arrow-icon" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <path d="M19 12H5"/>
                <path d="m12 19-7-7 7-7"/>
              </svg>
              Back to Login
            </aio-button>
          </form>
        }

        <app-auth-footer></app-auth-footer>
      </div>
    </div>
  </div>
</div>
